import axios from 'axios';

import { useAuthStore } from '../store/auth';
import { useCompanyStore } from '../store/company';
import { config } from '../config/env';

// Criar instância do Axios
const api = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  // Configurações de segurança
  withCredentials: false, // Não enviar cookies automaticamente
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  maxRedirects: 0, // Não seguir redirects automaticamente
  validateStatus: status => status >= 200 && status < 300, // Aceitar apenas sucessos
});

// Instância sem interceptors para refresh, evitando loops
const refreshApi = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: false,
  validateStatus: status => status >= 200 && status < 300,
});

// Função para sanitizar dados
const sanitizeData = data => {
  if (typeof data === 'string') {
    // Remover caracteres potencialmente perigosos
    return data.replace(/[<>]/g, '');
  }
  if (typeof data === 'object' && data !== null) {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      sanitized[key] = sanitizeData(value);
    }
    return sanitized;
  }
  return data;
};

// ------- Refresh Token (JWT) -------
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = callback => {
  refreshSubscribers.push(callback);
};

const onRefreshed = newToken => {
  refreshSubscribers.forEach(cb => {
    try {
      cb(newToken);
    } catch {
      // ignorar erros de callbacks
    }
  });
  refreshSubscribers = [];
};

const performTokenRefresh = async currentToken => {
  const headers = {};
  if (currentToken) {
    headers.Authorization = `Bearer ${currentToken}`;
  }
  const response = await refreshApi.post('/auth/refresh', {}, { headers });
  const data = response.data || {};
  if (!data.access_token) {
    const err = new Error('Refresh sem access_token');
    err.response = response;
    throw err;
  }
  return data.access_token;
};

// Interceptor para adicionar token de autenticação e company ID
api.interceptors.request.use(
  config => {
    const token = useAuthStore.getState().token;
    const companyId = useCompanyStore.getState().companyId;

    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 Auth Debug:', {
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 8) + '…' : 'null',
        hasCompanyId: !!companyId,
        url: config.url,
      });
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Nenhum token encontrado! Requisição será rejeitada pelo backend.');
      }
    }

    if (companyId && !config.headers['X-Company-Id']) {
      config.headers['X-Company-Id'] = companyId;
    }

    // Adicionar timestamp para evitar cache de requisições críticas
    if (config.method === 'get' && config.url?.includes('/auth/')) {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    // Sanitizar dados enviados (exceto FormData para uploads)
    if (config.data && !(config.data instanceof FormData)) {
      config.data = sanitizeData(config.data);
    }

    // Log de requisições em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      const safeHeaders = { ...(config.headers || {}) };
      if (safeHeaders.Authorization) {
        safeHeaders.Authorization = 'Bearer ***';
      }
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: safeHeaders,
        data: config.data,
      });
    }

    // Log específico para company-users (apenas desenvolvimento)
    if (process.env.NODE_ENV === 'development' && config.url?.includes('company-users')) {
      console.log('🔍 [API] Requisição para company-users:', {
        url: config.url,
        method: config.method,
        headers: config.headers,
        hasToken: !!config.headers.Authorization,
      });
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// Rate limiting simples
let requestCount = 0;
let lastReset = Date.now();
const RATE_LIMIT_WINDOW = 60000; // 1 minuto
const MAX_REQUESTS = 100; // Máximo de requisições por minuto

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  response => {
    // Log de respostas em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `[API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
      );
    }

    return response;
  },
  async error => {
    // Log de erros
    if (process.env.NODE_ENV === 'development') {
      console.error(
        `[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        error.response?.data,
      );
    }

    const originalRequest = error.config || {};

    // Rate limiting
    const now = Date.now();
    if (now - lastReset > RATE_LIMIT_WINDOW) {
      requestCount = 0;
      lastReset = now;
    }
    requestCount++;

    if (requestCount > MAX_REQUESTS) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Rate limit exceeded');
      }
      error.message = 'Muitas requisições. Aguarde alguns minutos.';
      return Promise.reject(error);
    }

    // Tratamento específico por status
    if (error.response?.status === 401) {
      const url = originalRequest?.url || '';
      const isAuthRoute =
        url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/2fa');

      // Não tentar refresh em rotas de auth
      if (!isAuthRoute && !originalRequest._retry) {
        originalRequest._retry = true;

        const authStore = useAuthStore.getState();
        const companyStore = useCompanyStore.getState();
        const currentToken = authStore.token;

        try {
          if (!isRefreshing) {
            isRefreshing = true;
            const newToken = await performTokenRefresh(currentToken);
            authStore.setToken(newToken);
            api.defaults.headers.Authorization = `Bearer ${newToken}`;
            isRefreshing = false;
            onRefreshed(newToken);
          }

          // Esperar até que o token atualizado esteja disponível
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh(token => {
              if (!token) {
                reject(error);
                return;
              }
              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${token}`,
              };
              resolve(api(originalRequest));
            });
          });
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshed(null);
          // Refresh falhou: limpar sessão com segurança
          try {
            authStore.clearToken();
            companyStore.clearCompany();
          } catch {}
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }
    }

    if (error.response?.status === 403) {
      // Sem permissão
      if (process.env.NODE_ENV === 'development') {
        console.error('Acesso negado:', error.response.data);
      }
      error.message = 'Acesso negado. Verifique suas permissões.';
    }

    if (error.response?.status === 422) {
      // Erro de validação
      if (process.env.NODE_ENV === 'development') {
        console.warn('Erro de validação:', error.response.data);
      }
    }

    if (error.response?.status === 429) {
      // Rate limit do servidor
      error.message = 'Muitas tentativas. Aguarde alguns minutos.';
    }

    if (error.response?.status >= 500) {
      // Erro do servidor
      error.message = 'Erro interno do servidor. Tente novamente mais tarde.';
    }

    // Verificar se é erro de rede
    if (!error.response && error.code === 'NETWORK_ERROR') {
      error.message = 'Erro de conexão. Verifique sua internet.';
    }

    return Promise.reject(error);
  },
);

export default api;
